import { Box, Dialog, TextField, TextFieldProps } from 'components'
import { CSSProperties, useRef, useState } from 'react'
import SimpleKeyboard, { KeyboardOptions } from 'react-simple-keyboard'
import 'react-simple-keyboard/build/css/index.css'
import { forceLight } from 'utils'

export interface Props extends Omit<TextFieldProps, 'onChange'> {
  inputRef?: HTMLElement | null
  open: boolean
  onClose: () => void
  keyboardOptions?: KeyboardOptions
  onChange: (value: string) => void
}

export default function Keyboard({
  inputRef,
  value,
  onChange,
  keyboardOptions,
  onClick,
  open,
  onClose,
  rightElement,
  ...rest
}: Props) {
  const [mode, setMode] = useState<KeyboardMode>('lower')
  const keyboardRef = useRef<any>(null)

  function handleKeyPress(button: string) {
    switch (button) {
      case '{enter}':
        return onClose()
      case '{shift}':
        return setMode(s => (s === 'lower' ? 'shift' : 'lower'))
      case '{lock}':
        return setMode(s => (s === 'lower' ? 'caps' : 'lower'))
      case '{space}':
        return setMode('shift')
    }

    if (mode === 'shift') setMode('lower')
  }

  return (
    <Dialog
      hideClose
      contentProps={{ style: addStyle(inputRef) }}
      placement="center"
      open={open}
      onClose={onClose}
    >
      <Box {...forceLight} borderRadius="12px" overflow="hidden">
        <TextField
          p={4}
          borderRadius="unset"
          variant="flushed"
          value={value}
          background="white"
          autoFocus
          onKeyDown={e => e.stopPropagation()}
          onChange={e => {
            keyboardRef.current.setInput(e.target.value)
            onChange(e.target.value)
          }}
          {...rest}
        />

        <SimpleKeyboard
          keyboardRef={r => (keyboardRef.current = r)}
          layoutName={mode === 'lower' ? 'default' : 'shift'}
          onKeyPress={handleKeyPress}
          onInit={k => {
            const v = String(value)
            if (!v.length) setMode('shift')
            k.setInput(v)
          }}
          onChange={v => {
            if (!v.length) setMode('shift')
            onChange(v)
          }}
          {...keyboardOptions}
        />
      </Box>
    </Dialog>
  )
}

type KeyboardMode = 'lower' | 'caps' | 'shift'

const keyboardWidth = 700

function addStyle(input?: HTMLElement | null) {
  let style: CSSProperties = {
    bottom: '15vh',
    position: 'absolute',
    margin: 0,
    maxWidth: keyboardWidth,
  }
  if (!input) return style

  const { left, width } = input.getBoundingClientRect()
  const toLeft = -(window.innerWidth - keyboardWidth) / 2
  const maxX = window.innerWidth - keyboardWidth - 20
  const x = toLeft + Math.min(left + width / 2 - keyboardWidth / 2, maxX)
  return { ...style, x }
}
